import {
  API_ROUTES,
  documentExtractionDetailSchema,
  documentExtractionSchema,
  extractionStatsSchema,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/**
 * Extraction queries.
 *
 * The one interesting behaviour here is polling: a running extraction has no
 * push channel, so the panel asks again while it is in flight and stops the
 * moment it settles. Polling a finished run forever would be a request per
 * second per open tab, for a value that can no longer change.
 */

export const extractionKeys = {
  document: (organizationId: string, documentId: string) =>
    ['extraction', organizationId, documentId] as const,
  latest: (organizationId: string, documentId: string) =>
    ['extraction', organizationId, documentId, 'latest'] as const,
  history: (organizationId: string, documentId: string) =>
    ['extraction', organizationId, documentId, 'history'] as const,
  stats: (organizationId: string) => ['extraction', organizationId, 'stats'] as const,
} as const;

export const latestExtractionQuery = (organizationId: string, documentId: string) =>
  queryOptions({
    queryKey: extractionKeys.latest(organizationId, documentId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.extraction.latest(organizationId, documentId),
        // Null is a legitimate answer: the document has not been read yet.
        schema: documentExtractionDetailSchema.nullable(),
        signal,
      }),
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 3_000 : false),
  });

export const extractionHistoryQuery = (organizationId: string, documentId: string) =>
  queryOptions({
    queryKey: extractionKeys.history(organizationId, documentId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.extraction.history(organizationId, documentId),
        schema: z.array(documentExtractionSchema),
        signal,
      }),
  });

export const extractionStatsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: extractionKeys.stats(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.extraction.stats(organizationId),
        schema: extractionStatsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export async function requestExtraction(
  organizationId: string,
  documentId: string,
  force: boolean,
): Promise<{ queued: boolean }> {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.extraction.request(organizationId, documentId),
    schema: z.object({ queued: z.boolean() }),
    body: { force },
  });
}
